import { useNuxtApp } from "#app"; // 导入 `useNuxtApp`
import {
  checkMallFetchUrl,
  getCurrentServer,
  getMockJwtByPrefixUrl,
  getMallApiBaseUrlFromStoreInfoMap,
  getMallAppToken,
  getMockUrl,
  getPrefixUrl,
  getCurrentSiteHost,
  getToken,
  isDisabledMallSsr,
  isMallProxyRequest,
  mockJwtMap,
  rewriteMallProxyUrl,
} from "@/utils/globalData"; // 导入 `useNuxtApp`

import { useMainStore } from "../composables/store";
import { filterGoodsListResponse } from "../utils/trafficItemFilter.js";

const FORWARDED_REQUEST_HEADER_BLACKLIST = new Set([
  "connection",
  "content-length",
  "keep-alive",
  "proxy-authenticate",
  "proxy-authorization",
  "te",
  "trailer",
  "transfer-encoding",
  "upgrade",
]);

function appendHeaders(target = {}, sourceHeaders = {}, { filterHopByHop = false } = {}) {
  if (!sourceHeaders) {
    return target;
  }

  const setHeader = (rawKey, rawValue) => {
    const key = String(rawKey || "").trim();
    if (!key || rawValue === undefined || rawValue === null || rawValue === "") {
      return;
    }

    const normalizedKey = key.toLowerCase();
    if (filterHopByHop && FORWARDED_REQUEST_HEADER_BLACKLIST.has(normalizedKey)) {
      return;
    }

    target[key] = Array.isArray(rawValue) ? rawValue.join(", ") : String(rawValue);
  };

  if (typeof sourceHeaders.forEach === "function") {
    sourceHeaders.forEach((value, key) => {
      setHeader(key, value);
    });
    return target;
  }

  Object.entries(sourceHeaders).forEach(([key, value]) => {
    setHeader(key, value);
  });

  return target;
}

function buildForwardHeaders(requestHeaders = {}, optionHeaders = {}) {
  const mergedHeaders = {};

  if (process.server) {
    appendHeaders(mergedHeaders, requestHeaders, { filterHopByHop: true });
  }

  appendHeaders(mergedHeaders, optionHeaders);

  return mergedHeaders;
}

function resolveDirectMallRequest(url, store, headers) {
  // disabledMallSsr 开启后，客户端 mall 请求直接命中 otherJson.mallApiUrl，
  // 避免继续走 Nuxt mall 代理。
  if (!process.client || !isDisabledMallSsr() || !isMallProxyRequest(url)) {
    return null;
  }

  const mallApiBaseUrl = getMallApiBaseUrlFromStoreInfoMap(
    store?.$state?.storeInfoDataMap,
    getCurrentServer(headers),
  );

  if (!mallApiBaseUrl) {
    return null;
  }

  return {
    requestUrl: rewriteMallProxyUrl(url),
    baseURL: mallApiBaseUrl,
  };
}

function removeMallJwtHeader(headers = {}) {
  if (!headers || typeof headers !== "object") {
    return headers;
  }

  // mall 侧接口不接受站点 openaccessjwt，这里统一在发送前剔除。
  if (typeof headers.delete === "function") {
    headers.delete("openaccessjwt");
    headers.delete("openAccessJwt");
  }

  Object.keys(headers).forEach((key) => {
    if (String(key || "").toLowerCase() === "openaccessjwt") {
      delete headers[key];
    }
  });
  return headers;
}

export function makeUnSetupFetch($fetch, headers, url, options) {
  const hostSite = getCurrentSiteHost(headers);
  const tokenKey = `openaccessjwt_${hostSite}`;
  const defaultHeaders = buildForwardHeaders(headers, options.headers || {});
  const store = useMainStore();
  const directMallRequest = resolveDirectMallRequest(url, store, headers);
  const requestUrl = directMallRequest?.requestUrl || url;
  const isMallRequest = checkMallFetchUrl(requestUrl);
  const prefixUrl = getPrefixUrl(requestUrl, headers);
  // openaccessjwt 只保留给站点/collection 请求，mall 请求统一不透传。
  if (!isMallRequest && getMockJwtByPrefixUrl(prefixUrl)) {
    defaultHeaders["openaccessjwt"] = getMockJwtByPrefixUrl(prefixUrl);
  }

  if (!isMallRequest && store && store.$state.tokenKeyMap[tokenKey]) {
    defaultHeaders["openaccessjwt"] = store.$state.tokenKeyMap[tokenKey];
  }

  if (isMallRequest) {
    removeMallJwtHeader(defaultHeaders);
  }


  let setOptions = {
    ...options,
    baseURL: directMallRequest?.baseURL || prefixUrl,
    headers: defaultHeaders,
  };

  if(process.client&&(!options.body||!options.body.unhaveToken)){
    let isMallUrl=isMallRequest;
    if(isMallUrl){
      setOptions.headers.authorization = getMallAppToken();
    }else{
      const token = getToken();
      if (typeof setOptions.headers != "undefined") {
        if (typeof token === "string") {
          setOptions.headers.authorization = token;
        } else {
          setOptions.headers.authorization = "";
        }
      }
    }
  }


  return $fetch(requestUrl, setOptions);
}

function makeCollectionMallFetch(url, options = {}) {
  const authToken = getMallAppToken() || getToken() || "";
  return useNuxtApp().$customFetch(`/collectionSite/mall${url}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      authorization: authToken,
    },
  });
}

/**
 * 获取轮播图信息
 
 * @param params
 * @returns {*}
 */
export function getBanner(params) {
  return useNuxtApp().$customFetch("/api/indexpic/list", {
    method: "GET",
    params,
  });
}

/**
 * 获取栏目
 
 * @param params
 * @returns {*}
 */
export function getColumn(params) {
  return useNuxtApp().$customFetch("/api/column/list", {
    method: "GET",
    params,
  });
}

/**
 * 获取某个资讯详情
 
 * @param params
 * @returns {*}
 */
export function getContentInfo(params) {
  return useNuxtApp().$customFetch("/api/content/info/" + params.id, {
    params,
  });
}

/**
 * 获取文章列表
 
 * @param params
 * @returns {*}
 */
export function getContentList(params) {
  return useNuxtApp().$customFetch("/api/content/list", {
    params: { ...params, limit: params.pageSize },
  });
}

/**
 * 分页获取友情链接
 
 * @param params
 * @returns {*}
 */
export function getConvenientList(params) {
  return useNuxtApp().$customFetch("/api/convenient/list", { params });
}

/**
 * 查询考生是否被录取
 
 * @param params
 * @returns {*}
 */
export function matriculatestat(params) {
  return useNuxtApp().$customFetch("/api/matriculate/stu/matriculatestate", {
    method: "POST",
    params,
  });
}

/**
 * 查询活动页
 
 * @param params
 * @returns {*}
 */
export function getActiveDetail(body = {}, $fetch, headers) {
  const requestBody = { ...(body || {}) };
  const getCache = requestBody.getCache ?? 1;
  delete requestBody.getCache;
  const requestUrl =
    "/platform/admin/manage/activityPage/activePageList?getCache=" + getCache;

  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      requestUrl,
      {
        method: "POST",
        params: requestBody,
        body: { ...requestBody, unhaveToken: 1 },
      }
    );
  }
  return useNuxtApp().$customFetch(requestUrl, {
    method: "POST",
    params: requestBody,
    body: { ...requestBody, unhaveToken: 1 },
  });
}

function resolveHelpCenterShopId(headers = {}, shopId = "") {
  const requestedShopId = String(
    shopId || headers?.["dp-shopId"] || headers?.["dp-shopid"] || "",
  ).trim();

  if (requestedShopId) {
    return requestedShopId;
  }

  const store = useMainStore();
  const currentServer = getCurrentServer(headers);
  const storeInfo = store?.$state?.storeInfoDataMap?.[currentServer]
    || Object.values(store?.$state?.storeInfoDataMap || {}).find((item) => item?.id)
    || {};

  return String(
    storeInfo?.id || storeInfo?.storePageInfo?.id || storeInfo?.dealerShopId || "",
  ).trim();
}

function requestHelpCenter(url, body = {}, $fetch, headers, shopId) {
  const resolvedShopId = resolveHelpCenterShopId(headers, shopId);
  const options = {
    method: "POST",
    body,
    headers: {
      "Content-Type": "application/json",
      ...(resolvedShopId ? { "dp-shopId": resolvedShopId } : {}),
    },
  };

  if ($fetch) {
    return makeUnSetupFetch($fetch, headers, url, options);
  }

  return useNuxtApp().$customFetch(url, options);
}

/**
 * 获取帮助中心分类树。
 */
export function getHelpCenterCategoryTree(params = {}, $fetch, headers, shopId) {
  return requestHelpCenter(
    "/platform/admin/manage/helpCenter/mall/categoryTree",
    params,
    $fetch,
    headers,
    shopId,
  );
}

/**
 * 分页获取指定帮助中心分类下的问题。
 */
export function getHelpCenterFaqList(params = {}, $fetch, headers, shopId) {
  return requestHelpCenter(
    "/platform/admin/manage/helpCenter/mall/faq/list",
    params,
    $fetch,
    headers,
    shopId,
  );
}

/**
 * 获取用户端帮助中心问题详情。
 */
export function getHelpCenterFaqDetail(params = {}, $fetch, headers, shopId) {
  return requestHelpCenter(
    "/platform/admin/manage/helpCenter/mall/faq/detail",
    params,
    $fetch,
    headers,
    shopId,
  );
}
/**
 * 查询参数
 
 * @param params
 * @returns {*}
 */
export function getArgument(params) {
  return useNuxtApp().$customFetch(
    "/platform/portal/sysArgument/queryArgument",
    {
      method: "POST",
      body: { params },
    }
  );
}

/**
 * 查询配置
 
 * @param params
 * @returns {*}
 */
export function getWebsiteHomePageInfo(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/platform/websiteHome/getWebsiteHomePageInfo?getCache=1&currentServer=" + params.currentServer,
      {
        method: "POST",
        body: { params },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
      }
    );
  }
  return useNuxtApp().$customFetch(
    "/platform/websiteHome/getWebsiteHomePageInfo?getCache=1&currentServer=" +
    params.currentServer,
    {
      method: "POST",
      body: { params },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
    }
  );
}

/**
 * 查询店铺配置
 
 * @param params
 * @returns {*}
 */
export function getWebStorePageInfo(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/admin/dealerShop/queryDealerShop?getCache=1",
      {
        method: "POST",
        params: { shopBackUrl: params.currentServer },
        body: { shopBackUrl: params.currentServer },
      }
    );
  }
  return useNuxtApp().$customFetch(
    "/admin/dealerShop/queryDealerShop?getCache=1",
    {
      method: "POST",
      params: { shopBackUrl: params.currentServer },
      body: { shopBackUrl: params.currentServer },
    }
  );
}

/**
 * 查询站点身份
 * @param params
 * @returns {*}
 */
export function querySiteIdentityByShopId(params) {
  return useNuxtApp().$customFetch(`/admin/dealerShop/querySiteIdentityByShopId`, {
    method: "GET",
    params,
  });
}


/**
 * 查询文章列表
 
 * @param params
 * @returns {*}
 */
export function getActiveListByTopicId($axios, params) {
  return useNuxtApp().$customFetch(
    "/admin/manage/article/queryArticleListByTopicId",
    {
      method: "POST",
      body: { params },
    }
  );
}

// 获取Tabbar配置
export function getTabBarConfigList(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/platform/admin/manage/tabBar/tabBarConfigList?getCache=1",
      {
        method: "POST",
        params,
        body: params,
      }
    );
  }
  return useNuxtApp().$customFetch(
    "/platform/admin/manage/tabBar/tabBarConfigList?getCache=1",
    {
      method: "POST",
      params,
      body: params,
    }
  );
}
// 获取文章详情
export function getArticleDetails(params) {
  return useNuxtApp().$customFetch(
    "/platform/admin/manage/article/articleList",
    {
      method: "POST",
      body: params,

    }
  );
}
/**
 * 查询文章列表
 
 * @param params
 * @returns {*}
 */
export function getActiveListFormPlatform(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/platform/admin/manage/article/articleList",
      {
        method: "POST",
        body: params,
      }
    );
  } else {
    return useNuxtApp().$customFetch(
      "/platform/admin/manage/article/articleList",
      {
        method: "POST",
        body: params,
      }
    );
  }
}

/**
 * 获取文章详情。接口会在成功返回详情时自动更新阅读数。
 */
export function getArticleDetail(params, $fetch, headers) {
  const requestUrl = "/platform/admin/manage/article/articleDetail";

  if ($fetch) {
    return makeUnSetupFetch($fetch, headers, requestUrl, {
      method: "POST",
      body: params,
    });
  }

  return useNuxtApp().$customFetch(
    requestUrl,
    {
      method: "POST",
      body: params,
    },
  );
}

/**
 * 查询tags列表
 
 * @param params
 * @returns {*}
 */
export function getTagsFormPlatform(params) {
  return useNuxtApp().$customFetch(
    "/platform/admin/tagDetail/tagDetailPageList",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 批量查询文章列表
 
 * @param params
 * @returns {*}
 */
export function mutiQueryArticleInCache($fetch, headers, params) {
  return makeUnSetupFetch(
    $fetch,
    headers,
    "/platform/admin/manage/article/mutiQueryArticleInCache",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 查询文章分类
 
 * @param params
 * @returns {*}
 */
export function getArticleCategoryList(params) {
  return useNuxtApp().$customFetch(
    "/platform/admin/manage/articleCategory/articleCategoryList",
    { method: "POST", body: params }
  );
}

export function queryTracking(params) {
  let setHeaders = {
    tracking_query_token: "823638A63866F824F91D64E236E06FCC",
  };
  if (
    window &&
    window.localStorage &&
    window.localStorage.getItem("tracking_query_meta")
  ) {
    setHeaders.tracking_query_meta = window.localStorage.getItem(
      "tracking_query_meta"
    );
  }
  return useNuxtApp().$customFetch("/mall-home/queryTracking", {
    method: "GET",
    params,
    headers: setHeaders,
  });
}

/**
 * 查询商品列表
 
 * @param params
 * @returns {*}
 */
export function getGoodsListFormPlatform($axios, url, params) {
  return useNuxtApp().$customFetch(url, params, {
    method: "POST",
    body: params,
    changeOrigin: true,
    headers: setHeaders,
  });
}


/**
 * 查询1688商品列表
 
 * @param params
 * @returns {*}
 */
export function get1688GoodsList(params, $fetch, headers) {
  const rawParam = params?.param && typeof params.param === "object" ? params.param : {};
  const normalizedBeginPage = Math.max(1, Number(rawParam.beginPage || 1) || 1);
  const nextParams = {
    ...params,
    param: {
      ...rawParam,
      beginPage: normalizedBeginPage,
    },
  };
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/alibabaGoods/productSearchKeywordQuery",
      {
        method: "POST",
        body: nextParams,
      }
    ).then((response) => filterGoodsListResponse(response, headers));
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/alibabaGoods/productSearchKeywordQuery",
      {
        method: "POST",
        body: nextParams,
      }
    ).then((response) => filterGoodsListResponse(response, headers));
  }
}


/**
 * 查询1688店铺商品列表
 
 * @param params
 * @returns {*}
 */
export function get1688SellerGoodsList(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/alibabaGoods/productSearchQuerySellerOfferList",
      {
        method: "POST",
        body: params,
      }
    ).then((response) => filterGoodsListResponse(response, headers));
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/alibabaGoods/productSearchQuerySellerOfferList",
      {
        method: "POST",
        body: params,
      }
    ).then((response) => filterGoodsListResponse(response, headers));
  }
}


/**
 * 查询1688商品列表从图片
 
 * @param params
 * @returns {*}
 */
export function get1688GoodsListByImg(params, $fetch, headers) {
  if ($fetch) {
    return makeUnSetupFetch(
      $fetch,
      headers,
      "/mall/admin/alibabaGoods/productSearchImageQuery",
      {
        method: "POST",
        body: params,
      }
    ).then((response) => filterGoodsListResponse(response, headers));
  } else {
    return useNuxtApp().$customFetch(
      "/mall/admin/alibabaGoods/productSearchImageQuery",
      {
        method: "POST",
        body: params,
      }
    ).then((response) => filterGoodsListResponse(response, headers));
  }
}


/**
 * 查询1688 图片
 * @param params
 * @returns {*}
 */
export function upload1688Img(params, $fetch, headers) {

  return useNuxtApp().$customFetch(
    "/mall/admin/alibabaGoods/productImageUpload",
    {
      method: "POST",
      body: params,
    }
  );

}


/**
 * 1688商品详情
 * @param params
 * @returns {*}
 */
export function get1688ProductDetail(params) {
  return useNuxtApp().$customFetch("/mall/admin/alibabaGoods/productSearchQueryProductDetail?mall=1", {
    method: "POST",
    body: params,
    headers:{

        "language":params.param.country,
        "transferKeysConfig":JSON.stringify([
        [
          "data.sellerLoginId",
        ],
        [
          "data.productShippingInfo.sendGoodsAddressText"
        ]
      ])

    }
  });
}

/**
 * 1688类目翻译
 * @param params
 * @returns {*}
 */
export function get1688CategoryTranslationById(params) {
  const requestHeaders = {
    ...(params?.headers || {}),
  };
  const requestLanguage =
    params?.param?.country
    || params?.param?.language
    || params?.country
    || params?.language;

  if (requestLanguage && !requestHeaders.language) {
    requestHeaders.language = requestLanguage;
  }

  return useNuxtApp().$customFetch("/mall/admin/alibabaGoods/categoryTranslationGetById", {
    method: "POST",
    body: params,
    headers: requestHeaders,
  });
}

/**
 * 加入购物车
 */
export function tbGlobalAddItemsToCart(params) {
  return useNuxtApp().$customFetch("/mall/admin/mallShoppingCart/add?mall=1", {
    method: "POST",
    body: params,
  });
}

function getTbGlobalLanguage() {
  try {
    const { $i18n } = useNuxtApp();
    const locale =
      ($i18n && typeof $i18n.locale === "function" ? $i18n.locale() : $i18n?.locale?.value) ||
      $i18n?.locale ||
      "zh-CN";
    const map = {
      en: "en",
      "vi_VN": "vi",
      vi: "vi",
      "ja_JP": "ja",
      ja: "ja",
      jp: "ja",
    };
    return map[locale] || null;
  } catch (e) {
    return null;
  }
}

/**
 * 淘宝商品详情（全球购）
 */
export function tbGlobalGoodsItemDetail(params) {
  const language = getTbGlobalLanguage();
  return useNuxtApp().$customFetch(
    "/mall/admin/overSeasTaoBao/traffic-item-get?mall=1&transfertw=true",
    {
      method: "POST",
      body: {
        ...params,
        language,
        param: {
          hideToastTip: 1,
          ...(params?.param || {}),
        },
      },
    }
  );
}

/**
 * 淘宝商品详情备用接口
 */
export function tbGlobalProductGet(params) {
  const language = getTbGlobalLanguage();
  return useNuxtApp().$customFetch("/mall/admin/overSeasTaoBao/product-get?mall=1", {
    method: "POST",
    body: {
      ...params,
      language,
      param: {
        hideToastTip: 1,
        ...(params?.param || {}),
      },
    },
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });
}

/**
 * 淘宝商品搜索/推荐（全球购）
 */
export function tbGlobalGoodsItemList(params) {
  const language = getTbGlobalLanguage();
  const innerParam = params?.param && typeof params.param === "object" ? params.param : {};
  return useNuxtApp()
    .$customFetch(
      "/mall/admin/overSeasTaoBao/traffic-item-search?mall=1&transfertw=true",
      {
        method: "POST",
        body: {
          ...params,
          ...innerParam,
          language,
          param: {
            hideToastTip: 1,
            ...innerParam,
          },
        },
      }
    )
    .then((response) => {
      const nextResponse = response && typeof response === "object" ? filterGoodsListResponse(response) : {};
      const nextData = nextResponse?.data && typeof nextResponse.data === "object" ? nextResponse.data : {};
      const hasTotal = Number(nextData.totalRecord || nextData.totalResults || nextData.total || 0) > 0;

      if (hasTotal) {
        return nextResponse;
      }

      return {
        ...nextResponse,
        data: {
          ...nextData,
          totalRecord: 1000,
        },
      };
    })
    .catch(() => {
      // 该接口偶发报错时返回兜底结构，避免影响页面上其他接口流程
      return {
        success: false,
        data: {
          dataList: [],
          totalRecord: 1000,
        },
      };
    });
}

/**
 * 淘宝图片上传（用于图片搜索）
 */
export function tbGlobalUploadImage(params) {
  return useNuxtApp().$customFetch("/mall/admin/overSeasTaoBao/upload-imageV2", {
    method: "POST",
    body:params,
  });
}

/**
 * 淘宝图片搜索商品列表
 */
export function tbGlobalGoodsItemImgSearch(params) {
  const language = getTbGlobalLanguage();
  const innerParam = params?.param && typeof params.param === "object" ? params.param : params || {};
  return useNuxtApp()
    .$customFetch(
      "/mall/admin/overSeasTaoBao/traffic-item-img-search?mall=1&transfertw=true",
      {
        method: "POST",
        body: {
          ...innerParam,
          language,
        },
        // headers: {
        //   "Content-Type": "application/json",
        //   locale: language || "zh-CN",
        // },
      }
    )
    .then((response) => filterGoodsListResponse(response))
    .catch(() => {
      return {
        success: false,
        data: {
          dataList: [],
          totalRecord: 0,
        },
      };
    });
}

/**
 * 淘宝购物车列表（用于获取数量等）
 */
export function tbGlobalItemsToCartList(params) {
  return useNuxtApp().$customFetch("/mall/admin/mallShoppingCart/list?mall=1", {
    method: "POST",
    body: params,
  });
}

/**
 * 淘宝下单渲染（全球购）v2
 */
export function tbGlobalPurchaseOrderRender2(params) {
  const appCurrencyCode = params?.appCurrencyCode || "CNY";
  return useNuxtApp().$customFetch(
    `/mall/admin/overSeasTaoBao/purchase-order-render2?mall=1&appCurrencyCode=${encodeURIComponent(
      appCurrencyCode
    )}`,
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 淘宝创建订单（全球购）v2
 */
export function createGlobalTbGoodsOrder2(params) {
  const appCurrencyCode = params?.appCurrencyCode || "CNY";
  return useNuxtApp().$customFetch(
    `/mall/admin/overSeasTaoBao/purchase-order-createV3?mall=1&appCurrencyCode=${encodeURIComponent(
      appCurrencyCode
    )}`,
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 淘宝创建订单（全球购）流程单
 */
export function createGlobalTbGoodsOrderProcess2(params) {
  const appCurrencyCode = params?.appCurrencyCode || "CNY";
  return useNuxtApp().$customFetch(
    `/mall/admin/overSeasTaoBao/process-purchase-order-create2?mall=1&appCurrencyCode=${encodeURIComponent(
      appCurrencyCode
    )}`,
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 淘宝订单批量支付（余额等）
 */
export function tbGlobalPurchaseOrderBatchPay(params) {
  return useNuxtApp().$customFetch(
    "/mall/admin/overSeasTaoBao/purchase-order-batchPayV3?mall=1",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 淘宝订单批量支付（流程单）
 */
export function tbGlobalPurchaseOrderBatchPayProcess(params) {
  return useNuxtApp().$customFetch(
    "/mall/admin/overSeasTaoBao/process-purchase-order-batchPay?mall=1",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 淘宝商品有效性校验（过期/下架）
 */
export function tbGlobalCheckProductExpire(params, options = {}) {
  return useNuxtApp().$customFetch(
    "/mall/admin/overSeasTaoBao/check-product-expire?mall=1",
    {
      method: "POST",
      body: params,
      ...(options || {}),
    }
  );
}

/**
 * 服务费比例
 */
export function queryPromotionScale(params) {
  return useNuxtApp().$customFetch(
    "/mall/admin/promotionProfile/queryPromotionScale?mall=1",
    {
      method: "POST",
      body: params,
    }
  );
}


/**
 * 查询仓库列表（同移动端：/dealer-shop-warehouse/query/warehouseList）
 */
export function getShopWarehouseInfoNew(params) {
  const shopId =
    params?.shopId ||
    params?.param?.shopId ||
    params?.params?.shopId ||
    "";
  const q = shopId ? `untransfer=true&shopId=${encodeURIComponent(shopId)}` : `untransfer=true`;
  return useNuxtApp().$customFetch(`mallSite/wareHouseManage/getWareHouseList?mall=1&${q}`, {
    method: "POST",
    body: params || {},
  });
}
/**
 * 微店商品详情
 * @param params {{ itemId: string }}
 */
export function getWeidianBuyerItemDetail(params) {
  return useNuxtApp().$customFetch("/mallSite/weidian/buyer/itemDetail", {
    method: "POST",
    body: params,
  });
}
/**
 * 查询仓库列表
 * @param params
 * @returns {*}
 */
export function getWarehouseList(params) {
  return useNuxtApp().$customFetch(`mallSite/wareHouseManage/getWareHouseList?mall=1`, {
    method: "POST",
    body: params,
  });
}

/**
 * 查询商城支付方式
 */
export function geyShopPayMode(params) {
  return makeCollectionMallFetch("/admin/dealerShopPaymentConfig/queryPageList", {
    method: "POST",
    body: params,
  });
}

/**
 * 根据订单编码查询采购支付单
 */
export function queryPurchaseOrderByCode(params) {
  return useNuxtApp().$customFetch(
    "/jyUserOrder/queryPurchaseOrderByCode?untransfer=true",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 采购单三方支付
 */
export function payJyUserPurchaseMallOrderV2(params) {
  return useNuxtApp().$customFetch(
    "/jyUserOrder/purchasepay/v2?untransfer=true",
    {
      method: "POST",
      body: params,
    }
  );
}

/**
 * 查询钱包余额
 */
export function queryFundInfoByUserId(params) {
  return makeCollectionMallFetch("/admin/fundInfo/queryByUserId", {
    method: "POST",
    body: params,
  });
}

/**
 * 解析商品地址
 */
export function getProductResolveLink(params) {
  let requestUrl = "/nestserver/ecommerce/links/resolve";
  const store = useMainStore();
  const currentServer = getCurrentServer();
  const mallSiteConfig = store?.getMallSiteConfig?.(currentServer) || {};
  requestUrl = mallSiteConfig?.productResolveLink || "/nestserver/ecommerce/links/resolve";

  return useNuxtApp().$customFetch(
    requestUrl,
    {
      method: 'POST',
      body: params,
    },
  )
}
